// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { RefreshCw, SearchX } from "lucide-react";
import { useGetAuditLog } from "@/hooks/useAudit";
import AuditLogRow from "@/components/organization/AuditLogRow";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import FilterChips from "@/components/common/FilterChips";
import { downloadCsv } from "@/utils/common.utils";
import SearchField from "@/components/common/SearchField";
import DateRangeFilter from "@/components/common/DateRangeFilter";
import ExportButton from "@/components/common/ExportButton";
import SectionCard from "@/components/common/SectionCard";
import AuditLogSkeleton from "@/components/organization/skeletons/AuditLogSkeleton";
import { EMPTY_ARRAY } from "@/utils/common.utils";
import { AUDIT_ACTION_GROUPS } from "@/constant/organization.constant";
import { getAuditActionBadgeClass } from "@/utils/organization.utils";
import { InternalRole } from "@/enum/common.enum";

export default function AuditLog() {
  const { context } = useAuth();
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState();

  const isOwner = context?.membership?.role === InternalRole.ORG_OWNER || context?.membership?.role === InternalRole.BROKERAGE_ADMIN;

  const { data, isLoading: loading, refetch } = useGetAuditLog(filter ? { action_prefix: filter } : {});
  const rows = data?.events ?? EMPTY_ARRAY;

  const onFilter = (k) => { setFilter(k); };

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const from = dateRange?.from ? startOfDay(dateRange.from).getTime() : null;
    const to = dateRange?.from ? endOfDay(dateRange.to || dateRange.from).getTime() : null;
    return rows.filter((row) => {
      const searchable = [row.action, row.actor_name, row.actor_email, row.target_type, row.target_id, JSON.stringify(row.payload || {})]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const timestamp = new Date(row.at).getTime();
      return (!query || searchable.includes(query)) && (!from || (timestamp >= from && timestamp <= to));
    });
  }, [rows, search, dateRange]);

  const exportRows = () => {
    if (!visibleRows.length) {
      toast.info("There are no audit events to export");
      return;
    }
    const rows = [
      ["Date", "Action", "Actor", "Email", "Target type", "Target ID"],
      ...visibleRows.map((row) => [row.at, row.action, row.actor_name, row.actor_email, row.target_type, row.target_id]),
    ];
    downloadCsv({ rows, filename: "audit-log.csv" });
  };

  if (!isOwner) return (
    <div className="mx-auto max-w-md px-6 py-32 text-center font-dashboard">
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-dash-faint">Restricted</div>
      <h1 className="text-3xl font-bold tracking-tight text-dash-ink">Audit log is admin-only.</h1>
      <p className="mt-3 text-sm text-dash-muted">Ask your org owner for access.</p>
    </div>
  );

  return (
    <div className="font-dashboard" data-testid="audit-log-page">
      <PageHeader
        title="Audit log"
        description="Review every important change made within your brokerage."
        className="mb-7"
        actions={
          <>
          <Button
            type="button"
            variant="dashboardOutline"
            onClick={() => refetch()}
            loading={loading}
            data-testid="audit-refresh"
          >
            <RefreshCw data-icon="inline-start" /> Refresh
          </Button>
          <ExportButton
            onExport={exportRows}
            size="default"
            testId="audit-export"
          />
          </>
        }
      />

      <div className="mb-6 flex flex-col items-start gap-3 xl:flex-row xl:items-start xl:justify-between">
        <FilterChips items={AUDIT_ACTION_GROUPS.map((group) => ({ value: group.key, label: group.label }))} value={filter} onValueChange={onFilter} scrollableOnMobile testIdPrefix="audit-filter" data-testid="audit-filters" />
        <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-start">
          <SearchField value={search} onChange={(event) => setSearch(event.target.value)} onClear={() => setSearch("")} placeholder="Search audit log..." className="w-full sm:w-64" inputClassName="h-11" testId="audit-search" />
          <DateRangeFilter value={dateRange} onChange={setDateRange} numberOfMonths={1} className="w-full sm:w-auto" testId="audit-date-range" />
        </div>
      </div>

      <SectionCard contentClassName="p-0" data-testid="audit-rows">
        {loading && (
          <AuditLogSkeleton rows={7} />
        )}
        {!loading && visibleRows.length === 0 && (
          <EmptyState compact icon={SearchX} title="No audit events found" description="Try changing the category, search term, or selected date range." className="border-0" />
        )}
        {!loading && visibleRows.map(r => (
          <AuditLogRow key={r.id} row={r} badgeClass={getAuditActionBadgeClass(r.action)} />
        ))}
      </SectionCard>

      <div className="mt-4 text-xs text-dash-faint">
        Scoped strictly to <span className="text-dash-ink">{context?.organization?.name}</span>. Other organizations' events are never visible here.
      </div>
    </div>
  );
}
